import React from "react";

const CustomTextField = ({
    size = "medium",
    placeholder = "",
    className = "",
    inputClassName = "",
    onChange,
    value = "",
    variant = "outlined",
    color = "gray",
    fullWidth = false,
    disabled = false,
    type = "text",
    name = "",
    id = "",
    multiline = false,
    rows = 4,
    maxRows = null,
    minRows = null,
    startIcon = null,
    ...props
}) => {
    // Size classes
    const sizeClasses = {
        small: "px-3 py-2 text-sm",
        medium: "px-4 py-3 text-base",
        large: "px-5 py-4 text-lg",
    };

    // Color classes
    const colorClasses = {
        gray: {
            outlined:
                "border-gray-300 focus:border-gray-500 focus:ring-gray-500",
            contained:
                "bg-gray-100 border-gray-200 focus:bg-white focus:border-gray-500 focus:ring-gray-500",
        },
        primary: {
            outlined: "border-gray-300 focus:border-primary focus:ring-primary",
            contained:
                "bg-gray-100 border-gray-200 focus:bg-white focus:border-primary focus:ring-primary",
        },
        error: {
            outlined: "border-red-300 focus:border-red-500 focus:ring-red-500",
            contained:
                "bg-red-50 border-red-200 focus:bg-white focus:border-red-500 focus:ring-red-500",
        },
    };

    // Variant classes
    const variantClasses = {
        outlined: "border bg-white",
        contained: "border bg-gray-100",
    };

    // Calculate textarea height based on rows
    const getTextareaHeight = () => {
        const lineHeight = size === "small" ? 20 : size === "large" ? 28 : 24; // Approximate line height
        const padding = size === "small" ? 16 : size === "large" ? 32 : 24; // Vertical padding
        const baseHeight = lineHeight * rows + padding;

        let height = baseHeight;

        if (minRows) {
            const minHeight = lineHeight * minRows + padding;
            height = Math.max(height, minHeight);
        }

        if (maxRows) {
            const maxHeight = lineHeight * maxRows + padding;
            height = Math.min(height, maxHeight);
        }

        return height;
    };

    // Auto-resize textarea
    const handleTextareaChange = (e) => {
        if (onChange) {
            onChange(e);
        }

        // Auto-resize functionality
        if (multiline && maxRows) {
            const textarea = e.target;
            textarea.style.height = "auto";
            const scrollHeight = textarea.scrollHeight;
            const lineHeight =
                size === "small" ? 20 : size === "large" ? 28 : 24;
            const padding = size === "small" ? 16 : size === "large" ? 32 : 24;
            const maxHeight = lineHeight * maxRows + padding;

            textarea.style.height = Math.min(scrollHeight, maxHeight) + "px";
        }
    };

    // Common props for both input and textarea
    const commonProps = {
        value,
        placeholder,
        disabled,
        name,
        id,
        ...props,
    };

    // Helper to merge classes manually without external packages
    const getMergedInputClasses = () => {
        let defaults = sizeClasses[size] || sizeClasses.medium;

        // If inputClassName contains a text size (e.g., text-lg, text-sm), remove the default text size
        if (
            inputClassName &&
            /\btext-(xs|sm|base|lg|[2-9]?xl)\b/.test(inputClassName)
        ) {
            defaults = defaults.replace(/\btext-(sm|base|lg)\b/g, "");
        }

        return `${defaults} ${inputClassName}`.trim();
    };

    // Render input or textarea based on multiline prop
    const renderInput = () => {
        if (multiline) {
            return (
                <textarea
                    {...commonProps}
                    onChange={handleTextareaChange}
                    className={`w-full bg-transparent border-none outline-hidden resize-none ${getMergedInputClasses()} ${
                        multiline ? "resize-y" : ""
                    }`}
                    rows={rows}
                    style={{
                        minHeight: getTextareaHeight(),
                        ...(maxRows && {
                            maxHeight: getTextareaHeight(),
                        }),
                    }}
                />
            );
        }

        return (
            <input
                {...commonProps}
                type={type}
                onChange={onChange}
                className={`w-full bg-transparent border-none outline-hidden ${getMergedInputClasses()}`}
            />
        );
    };

    return (
        <div className={fullWidth ? "w-full" : "w-auto"}>
            {startIcon ? (
                <div
                    className={`flex items-center rounded-md transition-all duration-200 ease-in-out ${
                        variantClasses[variant] || variantClasses.outlined
                    } ${
                        colorClasses[color]?.[variant] ||
                        colorClasses.gray.outlined
                    } ${
                        disabled
                            ? "opacity-50 cursor-not-allowed bg-gray-100"
                            : ""
                    } ${className}`}
                >
                    {/* Start Icon */}
                    <div className="shrink-0 pl-3">{startIcon}</div>

                    {/* Input/Textarea */}
                    <div className="flex-1">{renderInput()}</div>
                </div>
            ) : (
                <div
                    className={`rounded-md transition-all duration-200 ease-in-out ${
                        variantClasses[variant] || variantClasses.outlined
                    } ${
                        colorClasses[color]?.[variant] ||
                        colorClasses.gray.outlined
                    } ${
                        disabled
                            ? "opacity-50 cursor-not-allowed bg-gray-100"
                            : ""
                    } ${className}`}
                >
                    {renderInput()}
                </div>
            )}
        </div>
    );
};

export default CustomTextField;
